import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';

export const UserShape = ImmutablePropTypes.contains({
  name: PropTypes.string,
  email: PropTypes.string,
  headline: PropTypes.string,
  phone: PropTypes.string,
  pauseAll: PropTypes.bool,
  confirmationToken: PropTypes.string,
  password: PropTypes.string,
  loggedIn: PropTypes.bool,
  confirmed: PropTypes.bool,
  createdAt: PropTypes.number,
  updatedAt: PropTypes.number,
  loading: PropTypes.bool,
  tacksCount: PropTypes.number,
  activeTacksCount: PropTypes.number,
  finishedTacksCount: PropTypes.number,
  queuedTacksCount: PropTypes.number,
  slug: PropTypes.string,
  error: PropTypes.string,
  public: PropTypes.bool,
  followsCount: PropTypes.number,
  followersCount: PropTypes.number,
  points: PropTypes.number,
  hasActiveMobileDevice: PropTypes.bool
});

export const TackShape = ImmutablePropTypes.contains({
  currentInterval: PropTypes.number,
  day: PropTypes.number,
  lastSentDate: PropTypes.number,
  nextSendDate: PropTypes.number,
  done: PropTypes.bool,
  archiveSentAt: PropTypes.number,
  createdAt: PropTypes.number,
  slug: PropTypes.string,
  pausedAt: PropTypes.number,
  wordText: PropTypes.string,
  loading: PropTypes.bool,
  language: PropTypes.string,
  likedBy: PropTypes.object
});

export const ProfileShape = ImmutablePropTypes.contains({
  name: PropTypes.string,
  hasActiveSubscription: PropTypes.bool,
  slug: PropTypes.string,
  followedAt: PropTypes.number,
  tacksCount: PropTypes.number,
  createdAt: PropTypes.number,
  activeTacks: ImmutablePropTypes.listOf(TackShape),
  finishedTacks: ImmutablePropTypes.listOf(TackShape),
  error: ImmutablePropTypes.string,
  followersCount: ImmutablePropTypes.number,
  followsCount: ImmutablePropTypes.number
});

export const WordShape = ImmutablePropTypes.contains({
  wordText: PropTypes.string,
  pronunciationFile: PropTypes.string,
  exampleText: PropTypes.string,
  tacksCount: PropTypes.number,
  definitions: PropTypes.object,
  tackedBy: PropTypes.object,
  tackedByWithMetaData: PropTypes.object,
  mnemonicImageUrl: PropTypes.string,
  loading: PropTypes.bool,
  createdAt: PropTypes.number,
  updatedAt: PropTypes.number,
  lastTackedAt: PropTypes.number
});

export const WordSearchShape = ImmutablePropTypes.contains({
  searching: PropTypes.bool,
  suggestedWordTexts: ImmutablePropTypes.listOf(PropTypes.string)
});

export const PeopleSearchShape = ImmutablePropTypes.contains({
  searching: PropTypes.bool,
  suggestedPeople: ImmutablePropTypes.listOf(UserShape)
});

export const TrendingWordsShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  newestWords: ImmutablePropTypes.listOf(WordShape),
  mostTackedWords: ImmutablePropTypes.listOf(WordShape)
});

export const TackIntervalSelectionShape = ImmutablePropTypes.contains({
  tackInterval: PropTypes.number,
  isCorrect: PropTypes.bool,
  isTackDone: PropTypes.bool,
  nextSendDate: PropTypes.number,
  tackSlug: PropTypes.string,
  language: PropTypes.string,
  correctWordText: PropTypes.string,
  createdAt: PropTypes.number,
  error: PropTypes.string
});

export const UserTacksShape = ImmutablePropTypes.contains({
  retrieving: PropTypes.bool,
  done: ImmutablePropTypes.listOf(TackShape),
  notDone: ImmutablePropTypes.listOf(TackShape),
  queued: ImmutablePropTypes.listOf(TackShape)
});

export const RecommendedFollowsShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.string,
  recommendedUsers: ImmutablePropTypes.listOf(ProfileShape)
});

export const FeedItemShape = ImmutablePropTypes.contains({
  wordText: PropTypes.string,
  userName: PropTypes.string,
  userSlug: PropTypes.string,
  createdAt: PropTypes.number
});

export const FeedShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.string,
  followsFeed: ImmutablePropTypes.listOf(FeedItemShape)
});

export const FollowersShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.string,
  followers: ImmutablePropTypes.listOf(ProfileShape)
});

export const FollowsShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.string,
  followers: ImmutablePropTypes.listOf(ProfileShape)
});

export const SubscriptionShape = ImmutablePropTypes.contains({
  active: PropTypes.bool,
  loading: PropTypes.bool,
  createdAt: PropTypes.number,
  error: PropTypes.string,
  mobilePaymentSupported: PropTypes.bool,
  mobilePaymentMethod: PropTypes.string
});

export const TackNotificationItemShape = ImmutablePropTypes.contains({
  slug: PropTypes.string,
  wordText: PropTypes.string,
  deliveryMethod: PropTypes.string,
  tackInterval: PropTypes.number,
  day: PropTypes.number,
  isCheckpoint: PropTypes.bool,
  isChat: PropTypes.bool,
  readAt: PropTypes.number,
  createdAt: PropTypes.number
});

export const TackNotificationsShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.string,
  unreadCount: PropTypes.number,
  notifications: ImmutablePropTypes.listOf(TackNotificationItemShape)
});

export const PeopleNotificationItemShape = ImmutablePropTypes.contains({
  slug: PropTypes.string,
  deliveryMethod: PropTypes.string,
  peopleNotifiableType: PropTypes.string,
  peopleNotifiableSlug: PropTypes.string,
  triggeredByUserName: PropTypes.string,
  triggeredByUserHasActiveSubscription: PropTypes.bool,
  readAt: PropTypes.number,
  createdAt: PropTypes.number,
  likedBy: PropTypes.object
});

export const PeopleNotificationsShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.string,
  unreadCount: PropTypes.number,
  notifications: ImmutablePropTypes.listOf(PeopleNotificationItemShape)
});

export const TackNotificationDetailsShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  wordText: PropTypes.string,
  tackInterval: PropTypes.number,
  day: PropTypes.number,
  tacksCount: PropTypes.number,
  pronunciationFile: PropTypes.string,
  exampleText: PropTypes.string,
  definitions: PropTypes.object,
  recommendedWords: PropTypes.object,
  mnemonicImageUrl: PropTypes.string,
  isCheckpoint: PropTypes.bool,
  isArchive: PropTypes.bool,
  lastSentDate: PropTypes.number,
  readAt: PropTypes.number,
  slug: PropTypes.string,
  tackSlug: PropTypes.string,
  deliveryMethod: PropTypes.string,
  createdAt: PropTypes.number
});

export const NotificationPreferencesShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.string,
  deliveryMethod: PropTypes.string,
  stopEverything: PropTypes.bool,
  tackArchive: PropTypes.bool,
  newFollowers: PropTypes.bool,
  wordsFromPeopleFollowed: PropTypes.bool,
  points: PropTypes.bool,
  commentsFromPeopleFollowed: PropTypes.bool,
  likes: PropTypes.bool,
  commentMentions: PropTypes.bool
});

export const PointsLogItemShape = ImmutablePropTypes.contains({
  description: PropTypes.string,
  pointableEvent: PropTypes.string,
  points: PropTypes.number,
  createdAt: PropTypes.number
});

export const PointsLogShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.string,
  log: ImmutablePropTypes.listOf(PointsLogItemShape)
});

export const PointsLeaderboardItemShape = ImmutablePropTypes.contains({
  place: PropTypes.number,
  points: PropTypes.number,
  slug: PropTypes.string,
  userName: PropTypes.string
});

export const PointsLeaderboardShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.string,
  leaders: ImmutablePropTypes.listOf(PointsLeaderboardItemShape)
});

export const CommentItemShape = ImmutablePropTypes.contains({
  slug: PropTypes.string,
  userSlug: PropTypes.string,
  userName: PropTypes.string,
  content: PropTypes.string,
  wordText: PropTypes.string,
  createdAt: PropTypes.number,
  likedBy: PropTypes.object
});

export const CommentsShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.string,
  comments: ImmutablePropTypes.listOf(CommentItemShape)
});

export const SelectionOptionShape = ImmutablePropTypes.contains({
  slug: PropTypes.string,
  definitions: PropTypes.object
});

export const TackNotificationSelectionOptionsShape = ImmutablePropTypes.contains({
  loading: PropTypes.bool,
  error: PropTypes.string,
  selectionOptions: ImmutablePropTypes.listOf(SelectionOptionShape)
});

export const NavigationShape = ImmutablePropTypes.contains({
  pathname: PropTypes.string,
  lastPathname: PropTypes.string,
  afterTackPathname: PropTypes.string,
  lastScrollPosition: PropTypes.number,
  afterTackScrollPosition: PropTypes.number
});

// -------------- Yak Tack AI Chat -----------------
export const YakChatMessageShape = ImmutablePropTypes.contains({
  aiResponseMessage: PropTypes.string,
  userMessage: PropTypes.string,
  accuracy: PropTypes.number,
  guessesRemaining: PropTypes.number,
  guessesSoFar: PropTypes.number
});

export const YakChatShape = ImmutablePropTypes.contains({
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  messages: ImmutablePropTypes.listOf(YakChatMessageShape),
  accuracyOfLastAttempt: PropTypes.number,
  isChatSessionComplete: PropTypes.bool,
  lastGuessNumber: PropTypes.number
});



// WEBPACK FOOTER //
// ./src/shapes/index.js